/**
 * Searchable single-select combobox built on Radix Popover.
 *
 * Radix Popover handles all the awkward parts natively: portal,
 * floating positioning, focus trap, click-outside dismissal, and
 * coordination with Radix Dialog (both layers share the same
 * DismissableLayer stack, so clicking inside the Popover does NOT
 * close an enclosing Dialog). No portal/propagation hacks needed.
 *
 * Use for filter inputs whose value comes from a closed-ish set:
 * canonical types, entity types, audit event types, users, etc.
 *
 * Variants:
 *   - allowCustom={false} (default) → must pick from the list
 *   - allowCustom={true}            → user can submit free text not
 *                                     in the list (combobox-as-suggest)
 */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional second line, shown muted under the label. */
  description?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  allowCustom?: boolean;
  className?: string;
  /** Optional render hook for the displayed label when a value is set. */
  renderValue?: (option: ComboboxOption | null, raw: string | null) => React.ReactNode;
  id?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  loading,
  disabled,
  allowCustom = false,
  className,
  renderValue,
  id,
}: ComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  // Mouse-wheel scroll the option list. Inside a Radix Dialog, react-remove-scroll
  // installs a capture-phase wheel blocker on the document and the dropdown is
  // portalled OUTSIDE the dialog's allowed scroll area, so the browser's default
  // wheel scroll never reaches it. Drive scrollTop ourselves via a NON-passive
  // listener (so preventDefault sticks and we don't double-scroll elsewhere).
  React.useEffect(() => {
    const el = listRef.current;
    if (!el || !open) return;
    const onWheel = (e: WheelEvent) => {
      const atTop = el.scrollTop <= 0 && e.deltaY < 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
      if (!atTop && !atBottom) {
        el.scrollTop += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
    }
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  React.useEffect(() => {
    setHighlight(0);
  }, [query]);

  const selected = options.find((o) => o.value === value) ?? null;
  const displayLabel = selected?.label ?? value ?? "";

  function commit(next: string | null) {
    onChange(next);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) {
        commit(filtered[highlight].value);
      } else if (allowCustom && query.trim()) {
        commit(query.trim());
      }
    }
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-8 w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-2.5 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate text-left">
            {renderValue
              ? renderValue(selected, value)
              : displayLabel || placeholder || t("common.search")}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            {value && !disabled && (
              <span
                role="button"
                aria-label={t("common.cancel")}
                onPointerDown={(e) => {
                  // Stop the Popover trigger from toggling; just clear.
                  e.preventDefault();
                  e.stopPropagation();
                  commit(null);
                }}
                className="hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
          </span>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => {
            // Focus the search input, not the panel container.
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            "z-50 overflow-hidden rounded-md border border-border bg-elevated text-sm shadow-lg",
            // Match the trigger width exactly so the dropdown lines up.
            "w-[var(--radix-popover-trigger-width)]",
          )}
        >
          <div className="border-b border-border p-1">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder ?? placeholder ?? t("common.search")}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              className="h-7 w-full bg-transparent px-2 text-sm focus:outline-none"
            />
          </div>
          <ul ref={listRef} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {loading && (
              <li className="px-3 py-1.5 text-xs text-muted-foreground">
                {t("common.loading")}
              </li>
            )}
            {!loading && filtered.length === 0 && !allowCustom && (
              <li className="px-3 py-1.5 text-xs text-muted-foreground">
                {emptyText ?? t("pagination.empty", "No results")}
              </li>
            )}
            {!loading &&
              filtered.map((opt, i) => {
                const isHighlighted = i === highlight;
                const isSelected = opt.value === value;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => commit(opt.value)}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5",
                      "hover:bg-muted/70",
                      isHighlighted && "bg-accent/15",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{opt.label}</div>
                      {opt.description && (
                        <div className="truncate text-xs text-muted-foreground">
                          {opt.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Check
                        className="h-3.5 w-3.5 shrink-0 text-accent"
                        strokeWidth={1.5}
                      />
                    )}
                  </li>
                );
              })}
            {!loading &&
              allowCustom &&
              query.trim() &&
              !filtered.some((o) => o.value === query.trim()) && (
                <li
                  role="option"
                  onClick={() => commit(query.trim())}
                  className="cursor-pointer border-t border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                >
                  {t("combobox.useCustom", "Use")}{" "}
                  <span className="font-mono">"{query.trim()}"</span>
                </li>
              )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
